import CopyableId from "@/components/common/CopyableId";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, MoreHorizontal, Trash2, LogOut, Power, UserPlus, Clock } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import DataTable from "@/components/common/DataTable";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import PatientFormFields from "@/components/patients/PatientFormFields";
import PatientHotlineTab from "@/components/patients/PatientHotlineTab";
import PatientTeleconsultTab from "@/components/patients/PatientTeleconsultTab";

const EMPTY = {
  mrn: "", first_name: "", second_name: "", last_name: "", dob: "", gender: "",
  nationality: "", identity_type: "", identity_number: "",
  dial_code: "", phone: "", email: "",
  address: "", city: "", state: "", zip_code: "", location: {},
  emergency_contact: { first_name: "", last_name: "", dial_code: "", phone: "", email: "" },
  special_needs: ["N/A"], allergies: ["N/A"], language: "", requires_interpreter: null,
  facility_id: "", primary_diagnosis: "", admission_date: "",
  status: "active", notes: "",
};

// Disposition method → short label + accent color (mirrors disposition_meta.py)
const DISPOSITION_METHODS = {
  discharged: { label: "Discharged", color: "#059669" },
  left_program: { label: "Left Program", color: "#D97706" },
  transferred: { label: "Transferred", color: "#2563EB" },
  deceased: { label: "Deceased", color: "#6B7280" },
  inactive: { label: "Inactive", color: "#64748B" },
};

// A patient is conceptually either Active or Dispositioned (with the exit method shown as a chip).
function PatientStatusCell({ status, disposition }) {
  if (status === "active") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: "#10B98114", color: "#10B981" }} data-testid="patient-status-active">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#10B981" }} />Active
      </span>
    );
  }
  const type = disposition?.type || status;
  const m = DISPOSITION_METHODS[type] || { label: disposition?.label || "Exited", color: "#6B7280" };
  return (
    <div className="flex items-center gap-1.5" data-testid="patient-status-dispositioned">
      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: "#F1F5F9", color: "#475569" }}>Dispositioned</span>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: `${m.color}1A`, color: m.color }} data-testid="patient-status-dispo-method" title={disposition?.label || m.label}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />{m.label}
      </span>
    </div>
  );
}

export default function PatientsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [facilityFilter, setFacilityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [tab, setTab] = useState("profile");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nidConflict, setNidConflict] = useState("");
  const [timeline, setTimeline] = useState([]);
  const [lookups, setLookups] = useState({ nationality: [], special_needs: [], patient_allergies: [] });

  const [confirm, setConfirm] = useState({ open: false });

  async function load() {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.q = search;
      if (statusFilter !== "all") params.status = statusFilter;
      if (facilityFilter !== "all") params.facility_id = facilityFilter;
      const res = await api.get("/patients", { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, sortBy, sortDir, statusFilter, facilityFilter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); }, [search]);

  useEffect(() => {
    api.get("/facilities", { params: { page_size: 50 } }).then((r) => setFacilities(r.data.items)).catch(() => {});
    const codes = ["nationality", "special_needs", "patient_allergies"];
    Promise.all(codes.map((c) => api.get(`/forms/lookup-options/${c}`).then((r) => [c, r.data.items]).catch(() => [c, []])))
      .then((pairs) => setLookups(Object.fromEntries(pairs)));
  }, []);

  const isSaudi = (form.nationality || "").toLowerCase() === "saudi";

  function computeErrors(f) {
    const e = {};
    const nameErr = (v, label) => {
      const s = (v || "").trim();
      if (!s) return `${label} is required`;
      if (s.length < 2 || s.length > 30) return `${label} must be 2–30 characters`;
      if (!/^[A-Za-z\u0600-\u06FF][A-Za-z\u0600-\u06FF\s'-]{1,29}$/.test(s)) return `${label} shouldn't include numbers or special characters`;
      return null;
    };
    const emailErr = (v) => {
      const s = (v || "").trim();
      if (!s) return "Email is required";
      if (!/^\S+@\S+\.\S+$/.test(s)) return "Enter a valid email address";
      if (s.length < 5 || s.length > 30) return "Email must be 5–30 characters";
      return null;
    };
    const phoneErr = (dial, phone) => {
      const p = (phone || "").trim();
      if (!p) return dial ? "Phone number is required" : "Select a country dial code first";
      if (!dial) return "Select a country dial code first";
      if (!/^\d{6,25}$/.test(p)) return "Phone number must be 6–25 digits";
      return null;
    };
    e.first_name = nameErr(f.first_name, "First name");
    e.second_name = nameErr(f.second_name, "Second name");
    e.last_name = nameErr(f.last_name, "Last name");
    e.email = emailErr(f.email);
    if (!f.dial_code) e.dial_code = "The field is required";
    e.phone = phoneErr(f.dial_code, f.phone);
    if (!f.identity_type) e.identity_type = "The field is required";
    // Nationality ⇄ identity type consistency
    if (f.nationality) {
      const saudi = (f.nationality || "").toLowerCase() === "saudi";
      if (saudi && f.identity_type && f.identity_type !== "National Number") e.identity_type = "Saudi nationals must use a National Number";
      if (!saudi && f.identity_type === "National Number") e.identity_type = "National Number is only valid for Saudi nationals";
    }
    // identity number
    const itype = f.identity_type, inum = (f.identity_number || "").trim();
    if (!itype) e.identity_number = "Select identity type first";
    else if (!inum) e.identity_number = "The field is required";
    else if (itype === "National Number" && !/^1\d{9}$/.test(inum)) e.identity_number = "National Number must start with 1 and be exactly 10 digits";
    else if (itype === "Permanent Resident" && !/^2\d{9}$/.test(inum)) e.identity_number = "Permanent Resident must start with 2 and be exactly 10 digits";
    else if (itype === "Border Number" && !/^[34]\d{9}$/.test(inum)) e.identity_number = "Border Number must be 10 digits starting with 3 or 4";
    else if (itype === "Passport" && !/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]{5,30}$/.test(inum)) e.identity_number = "Passport must be 5–30 chars, a mix of letters and numbers";
    // dob
    if (!f.dob) e.dob = "The field is required";
    else if (f.dob > new Date().toISOString().slice(0, 10)) e.dob = "Date of birth can't be in the future";
    else {
      const age = Math.floor((Date.now() - new Date(f.dob).getTime()) / (365.25 * 864e5));
      if (age < 20) e.dob = "Patient should be at least 20 years of age";
    }
    if (!f.nationality) e.nationality = "The field is required";
    if (!f.gender) e.gender = "The field is required";
    const _addr = (f.address && typeof f.address === "object") ? (f.address.line1 || "") : (f.address || "");
    if (!String(_addr).trim()) e.address = "The field is required";
    if (!(f.zip_code || "").trim()) e.zip_code = "The field is required";
    // emergency contact
    const ec = f.emergency_contact || {};
    e.ec_first_name = nameErr(ec.first_name, "First name");
    e.ec_last_name = nameErr(ec.last_name, "Last name");
    if (!ec.dial_code) e.ec_dial_code = "The field is required";
    e.ec_phone = phoneErr(ec.dial_code, ec.phone);
    e.ec_email = emailErr(ec.email);
    // clinical
    if (!f.language) e.language = "The field is required";
    if (f.requires_interpreter === null || f.requires_interpreter === undefined) e.requires_interpreter = "The field is required";
    // strip nulls
    Object.keys(e).forEach((k) => { if (!e[k]) delete e[k]; });
    return e;
  }

  useEffect(() => {
    if (!sheetOpen) return;
    const all = computeErrors(form);
    const visible = submitAttempted ? all : Object.fromEntries(Object.entries(all).filter(([k]) => touched[k]));
    setErrors((prev) => {
      const merged = { ...visible };
      if (prev.identity_number && !all.identity_number && prev.identity_number.includes("already exists")) merged.identity_number = prev.identity_number;
      if (nidConflict && !all.identity_number) merged.identity_number = nidConflict;
      return merged;
    });
    // eslint-disable-next-line
  }, [form, touched, submitAttempted, sheetOpen, nidConflict]);

  // Live uniqueness check on the identity number (no other patient may possess it).
  useEffect(() => {
    if (!sheetOpen) { setNidConflict(""); return; }
    const inum = (form.identity_number || "").trim();
    if (!inum || computeErrors(form).identity_number) { setNidConflict(""); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/patients/check-identity", {
          params: { identity_number: inum, exclude_id: editing?.id || "" },
        });
        setNidConflict(res.data.unique ? "" : (res.data.message || "This identity number already belongs to another patient."));
      } catch (_e) { setNidConflict(""); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form.identity_number, form.identity_type, editing, sheetOpen]);

  function setField(name, value) {
    setForm((f) => {
      const next = { ...f, [name]: value };
      // Nationality drives the allowed identity type (Saudi → National Number; others exclude it)
      if (name === "nationality") {
        const saudi = (value || "").toLowerCase() === "saudi";
        if (saudi) next.identity_type = "National Number";
        else if (next.identity_type === "National Number") next.identity_type = "";
      }
      return next;
    });
    setTouched((t) => ({ ...t, [name]: true }));
  }
  function setEc(name, value) {
    setForm((f) => ({ ...f, emergency_contact: { ...(f.emergency_contact || {}), [name]: value } }));
    setTouched((t) => ({ ...t, ["ec_" + name]: true }));
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
    setTab("profile");
    setSheetOpen(true);
  }
  async function openEdit(row) {
    setEditing(row);
    const normGender = (g) => {
      const s = String(g || "").trim().toLowerCase();
      if (["female", "f"].includes(s)) return "female";
      if (["male", "m"].includes(s)) return "male";
      if (["other", "others", "o"].includes(s)) return "other";
      return row.gender || "";
    };
    const normPhone = (p) => String(p || "").replace(/\D/g, "");
    const ec = row.emergency_contact || { first_name: "", last_name: "", dial_code: "", phone: "", email: "" };
    setForm({
      mrn: row.mrn, first_name: row.first_name, second_name: row.second_name || "", last_name: row.last_name,
      dob: row.dob ? String(row.dob).slice(0, 10) : "", gender: normGender(row.gender),
      nationality: row.nationality || "", identity_type: row.identity_type || "", identity_number: row.identity_number || "",
      dial_code: row.dial_code || "", phone: normPhone(row.phone), email: row.email || "",
      address: (row.address && typeof row.address === "object") ? (row.address.line1 || "") : (row.address || ""), city: row.city || "", state: row.state || "", zip_code: row.zip_code || "", location: (row.address && typeof row.address === "object") ? row.address : (row.address ? { line1: row.address, city: row.city || "" } : {}),
      emergency_contact: { ...ec, phone: normPhone(ec.phone) },
      special_needs: row.special_needs && row.special_needs.length ? row.special_needs : ["N/A"],
      allergies: row.allergies && row.allergies.length ? row.allergies : ["N/A"],
      language: row.language || "", requires_interpreter: row.requires_interpreter ?? null,
      facility_id: row.facility_id || "", primary_diagnosis: row.primary_diagnosis || "",
      admission_date: row.admission_date || "", status: row.status || "active", notes: row.notes || "",
    });
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
    setTab("profile");
    setSheetOpen(true);
    try {
      const tl = await api.get(`/patients/${row.id}/timeline`);
      setTimeline(tl.data.items || []);
    } catch (_e) { setTimeline([]); }
  }

  function validate() {
    setSubmitAttempted(true);
    const e = computeErrors(form);
    if (nidConflict && !e.identity_number) e.identity_number = nidConflict;
    setErrors(e);
    if (Object.keys(e).length > 0) toast.error("Please fix the highlighted fields before saving.");
    return Object.keys(e).length === 0;
  }

  async function save() {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Send the structured address object (line1 + district + geo pin) so the patient
      // appears on the Fleet live map. Falls back to plain string if no structured data.
      if (form.location && Object.keys(form.location).length) {
        payload.address = { ...form.location, line1: String(form.address || form.location.line1 || "").trim(), city: form.city || form.location.city };
      } else if (editing && editing.address && typeof editing.address === "object") {
        payload.address = { ...editing.address, line1: String(form.address || "").trim() };
      }
      delete payload.location;
      if (!payload.special_needs || payload.special_needs.length === 0) payload.special_needs = ["N/A"];
      if (!payload.allergies || payload.allergies.length === 0) payload.allergies = ["N/A"];
      if (editing) {
        delete payload.mrn;
        await api.put(`/patients/${editing.id}`, payload);
        toast.success("Patient updated");
      } else {
        delete payload.mrn; // server-generated
        await api.post("/patients", payload);
        toast.success("Patient created");
      }
      setSheetOpen(false);
      load();
    } catch (e) {
      const fe = e?.response?.data?.detail?.field_errors;
      if (fe && typeof fe === "object") { setErrors((prev) => ({ ...prev, ...fe })); setSubmitAttempted(true); }
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function doAction(row, action) {
    try {
      if (action === "delete") {
        await api.delete(`/patients/${row.id}`);
        toast.success("Patient archived");
      } else {
        await api.post(`/patients/${row.id}/${action}`);
        toast.success(`Patient ${action}d`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const facilityName = (id) => facilities.find((f) => f.id === id)?.name || "—";

  const columns = [
    {
      key: "name", label: "Patient",
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-[#1F2937] truncate">{r.first_name} {r.last_name}</div>
          <div className="text-xs text-[#6B7280]"><CopyableId value={r.mrn} kind="MRN" /> · {r.gender || "—"} · DOB {r.dob || "—"}</div>
        </div>
      ),
    },
    { key: "primary_diagnosis", label: "Primary diagnosis", sortable: false, render: (r) => r.primary_diagnosis || "—" },
    { key: "facility_id", label: "Facility", render: (r) => <span className="text-sm text-[#1F2937]">{facilityName(r.facility_id)}</span> },
    { key: "admission_date", label: "Admitted", sortable: true, render: (r) => r.admission_date || "—" },
    { key: "status", label: "Status", sortable: true, render: (r) => <PatientStatusCell status={r.status} disposition={r.disposition} /> },
    {
      key: "actions", label: "",
      render: (r) => {
        const isDispositioned = r.status === "dispositioned" || !!r.disposition;
        return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`patient-actions-${r.id}`} onClick={(e) => e.stopPropagation()} className="w-8 h-8 rounded-md hover:bg-[#F5F7FA] flex items-center justify-center">
              <MoreHorizontal className="w-4 h-4 text-[#6B7280]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`patient-edit-${r.id}`}>
              <Edit className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
            {!isDispositioned && (
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); navigate(`/clinical/patients/${r.id}?tab=disposition`); }}
                data-testid={`patient-disposition-${r.id}`}
              >
                <LogOut className="w-4 h-4 mr-2" /> Disposition
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {r.status !== "active" ? (
              <DropdownMenuItem onClick={() => doAction(r, "activate")}><Power className="w-4 h-4 mr-2" /> Activate</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => doAction(r, "deactivate")}><Power className="w-4 h-4 mr-2" /> Deactivate</DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-[#EF4444]"
              data-testid={`patient-delete-${r.id}`}
              onClick={() => setConfirm({
                open: true, title: "Archive patient", description: `Archive ${r.first_name} ${r.last_name}?`,
                onConfirm: () => doAction(r, "delete"), destructive: true,
              })}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Archive
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div data-testid="patients-page">
      <PageHeader
        title="Patient Management"
        subtitle="Census, care team assignment, and patient lifecycle across the network."
        actions={
          <Button onClick={openCreate} data-testid="create-patient-button" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
            <UserPlus className="w-4 h-4 mr-1.5" /> Admit Patient
          </Button>
        }
      />

      <DataTable
        testId="patients-table"
        columns={columns}
        data={items}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        onPageChange={setPage}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        sortBy={sortBy} sortDir={sortDir}
        exportFilename="patients.csv"
        filters={
          <>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px] h-9 border-[#E5E7EB]" data-testid="patient-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="dispositioned">Dispositioned</SelectItem>
              </SelectContent>
            </Select>
            <Select value={facilityFilter} onValueChange={(v) => { setFacilityFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[200px] h-9 border-[#E5E7EB]" data-testid="patient-facility-filter">
                <SelectValue placeholder="Facility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All facilities</SelectItem>
                {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        }
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[720px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>{editing ? `${editing.first_name} ${editing.last_name}` : "Admit patient"}</SheetTitle>
            <SheetDescription>{editing ? `MRN ${editing.mrn}` : "Capture admission details for a new patient."}</SheetDescription>
          </SheetHeader>

          {editing ? (
            <Tabs value={tab} onValueChange={setTab} className="p-6">
              <TabsList className="bg-[#F5F7FA]">
                <TabsTrigger value="profile" data-testid="patient-tab-profile">Profile</TabsTrigger>
                <TabsTrigger value="timeline" data-testid="patient-tab-timeline">Timeline</TabsTrigger>
                <TabsTrigger value="hotline" data-testid="patient-tab-hotline">Nurse Hotline</TabsTrigger>
                <TabsTrigger value="teleconsult" data-testid="patient-tab-teleconsult">Teleconsult</TabsTrigger>
                <TabsTrigger value="audit" data-testid="patient-tab-audit">Audit</TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="mt-4 space-y-4">
                <PatientFormFields form={form} setField={setField} setEc={setEc} setForm={setForm} errors={errors} facilities={facilities} lookups={lookups} isSaudi={isSaudi} editing />
              </TabsContent>

              <TabsContent value="timeline" className="mt-4">
                <div className="hah-card p-5">
                  <h3 className="text-base font-semibold text-[#1F2937] mb-4">Patient timeline</h3>
                  <ol className="relative border-l border-[#E5E7EB] ml-3 space-y-5">
                    {timeline.length === 0 && <li className="text-sm text-[#6B7280] pl-4">No events yet.</li>}
                    {timeline.map((t) => (
                      <li key={t.id} className="ml-4">
                        <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-[#0B7E8C] ring-4 ring-white" />
                        <div className="flex items-center gap-2">
                          <StatusBadge status={t.action.toLowerCase()} label={t.action} />
                          <span className="text-xs text-[#6B7280]">{new Date(t.created_at).toLocaleString()}</span>
                        </div>
                        <div className="text-sm text-[#1F2937] mt-1">{t.user_email || "system"}</div>
                      </li>
                    ))}
                  </ol>
                </div>
              </TabsContent>

              <TabsContent value="hotline" className="mt-4">
                <PatientHotlineTab patientId={editing.id} />
              </TabsContent>

              <TabsContent value="teleconsult" className="mt-4">
                <PatientTeleconsultTab patientId={editing.id} />
              </TabsContent>

              <TabsContent value="audit" className="mt-4">
                <div className="hah-card p-5">
                  <h3 className="text-base font-semibold text-[#1F2937] mb-3">Audit history</h3>
                  {timeline.length === 0 ? <div className="text-sm text-[#6B7280]">No audit events.</div> :
                    <ul className="space-y-2 text-sm">
                      {timeline.map((t) => (
                        <li key={t.id} className="flex items-center justify-between border-b border-[#E5E7EB] pb-2">
                          <span className="flex items-center gap-2"><Clock className="w-3 h-3 text-[#94A3B8]" /> {new Date(t.created_at).toLocaleString()}</span>
                          <span className="text-[#6B7280]">{t.user_email}</span>
                          <StatusBadge status={t.action.toLowerCase()} label={t.action} />
                        </li>
                      ))}
                    </ul>
                  }
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="p-6 space-y-4">
              <PatientFormFields form={form} setField={setField} setEc={setEc} setForm={setForm} errors={errors} facilities={facilities} lookups={lookups} isSaudi={isSaudi} />
            </div>
          )}

          <StickyActionBar>
            <Button variant="outline" onClick={() => setSheetOpen(false)} className="border-[#E5E7EB]" data-testid="patient-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="patient-form-save" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
              {saving ? "Saving…" : editing ? "Save changes" : "Admit patient"}
            </Button>
          </StickyActionBar>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title}
        description={confirm.description}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }}
        destructive={confirm.destructive}
      />
    </div>
  );
}
